import { useCallback, useEffect, useRef } from "react";

import { jsxToNode } from "@/components/map-pin";
import GenericPin from "@/components/map-pin/generic-pin";
import MapKitScript from "@/components/mapkit-script";
import { useMapKitLoad } from "@/hooks/use-map-kit-load";
import { cn } from "@/lib/utils";

import type { LocationType } from "../types";

export interface MapProps {
  className?: string;
  locations: LocationType[];
}

export default function MapKit({ className, locations }: MapProps) {
  const { isMapInitialized, mapRef, mapContainerRef, handleMapKitLoad } =
    useMapKitLoad();
  const markersRef = useRef<mapkit.Annotation[]>([]);

  const addAnnotations = useCallback(() => {
    if (!mapRef.current) {
      return;
    }
    markersRef.current = locations
      .map((location) => {
        if (!location.location) {
          return null;
        }

        const [lng, lat] = location.location.coordinates;
        // biome-ignore lint/correctness/noUndeclaredVariables: legacy
        const marker = new mapkit.Annotation(
          // biome-ignore lint/correctness/noUndeclaredVariables: legacy
          new mapkit.Coordinate(lat, lng),
          (_coordinate, _options) => jsxToNode(<GenericPin />),
          {
            size: { height: 26, width: 26 },
          }
        );
        return marker;
      })
      .filter(Boolean) as mapkit.Annotation[];

    mapRef.current.showItems(markersRef.current, {
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      padding: new mapkit.Padding(75, 75, 75, 75),
    });
  }, [locations, mapRef.current]);

  useEffect(() => {
    if (!(isMapInitialized && mapContainerRef.current) || !!mapRef.current) {
      return;
    }

    // biome-ignore lint/correctness/noUndeclaredVariables: legacy
    mapRef.current = new mapkit.Map(mapContainerRef.current, {
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      colorScheme: mapkit.Map.ColorSchemes.Dark,
      isRotationEnabled: false,
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      mapType: mapkit.Map.MapTypes.MutedStandard,
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      showsCompass: mapkit.FeatureVisibility.Hidden,
      showsMapTypeControl: false,
    });

    addAnnotations();
    // biome-ignore lint/suspicious/noExplicitAny: legacy
    (mapRef.current as any)._allowWheelToZoom = true;
  }, [isMapInitialized, addAnnotations, mapContainerRef.current, mapRef]);

  return (
    <>
      <div
        className={cn(
          "h-118 [&_canvas]:contrast-140 [&_canvas]:grayscale",
          className
        )}
        ref={mapContainerRef}
      />
      <MapKitScript onLoad={handleMapKitLoad} />
    </>
  );
}
